import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CapsuleButton } from "../../Common/_components";
import { Icons } from "../../Common/_assets/icons/Icons";
import { CustomSearchBar } from "../../Everou/_components";
import { CustomModal } from "../../Everou/_components/CustomModal";
import { Selectors } from "../../Everou/_reducers/app.reducer";
import { OrganizationsActions } from "../../Organizations/_actions/OrganizationsActions";
import {
    OrganizationDetail,
    OrganizationLocationRef,
} from "../../Organizations/_models/Organization";

const SEARCH_DEBOUNCE_MS = 400;

export function PMSOrganizationLocations() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const selectedUid: string | null = useSelector(
        Selectors.getOrganizationSelectedUid,
    );
    const detail: OrganizationDetail | null = useSelector(
        Selectors.getOrganizationSelectedDetail,
    );
    const available: OrganizationLocationRef[] = useSelector(
        Selectors.getOrganizationAvailableLocations,
    );
    const availableTotal: number = useSelector(
        Selectors.getOrganizationAvailableLocationsTotal,
    );
    // Filter the stored page belongs to: the source of truth for load-more
    // and add-all, so they always act on exactly what the list shows even
    // while a newly typed search is still debouncing/loading.
    const storedSearchTerm: string | null = useSelector(
        Selectors.getOrganizationAvailableLocationsSearchTerm,
    );

    const [isAddOpen, setIsAddOpen] = useState(false);
    const [search, setSearch] = useState("");
    const searchTimer = useRef<any>(null);
    // True from the keystroke until the search it triggered has landed
    // (debounce included). Load-more must not run meanwhile: it would ask for
    // the next page of storedSearchTerm — still the *previous* filter, since
    // the new one has not landed — and being the newer request it would win
    // the requestId race, so the search the user is waiting for would be
    // discarded by the reducer and never retried, freezing the list on the
    // abandoned filter while the search bar shows the new one.
    const [isSearching, setIsSearching] = useState(false);
    // Tags each search so only the latest one clears the flag: an older,
    // slower search resolving afterwards must not re-enable load-more while
    // the newest is still in flight.
    const searchSeq = useRef(0);

    function cancelPendingSearch() {
        if (searchTimer.current) {
            clearTimeout(searchTimer.current);
            searchTimer.current = null;
        }
        // Whatever was in flight is abandoned: its response must not clear
        // the flag for whoever comes next. Only the ref is touched here, so
        // this stays safe to call from the unmount cleanup.
        searchSeq.current++;
    }

    // A pending debounced search must never outlive the modal: it would win
    // the request race and leave the store filtered by an abandoned term.
    useEffect(() => cancelPendingSearch, []);

    function openAdd() {
        if (!selectedUid) return;
        cancelPendingSearch();
        setIsSearching(false);
        setSearch("");
        // Drop the previous session's page so its filtered list (and its
        // total in the add-all button) can't show while the fresh one loads.
        dispatch(OrganizationsActions.clearAvailableLocations(selectedUid));
        dispatch(OrganizationsActions.getAvailableLocations(selectedUid));
        setIsAddOpen(true);
    }

    function closeAdd() {
        cancelPendingSearch();
        setIsSearching(false);
        setIsAddOpen(false);
    }

    function handleSearch(value: string) {
        setSearch(value);
        cancelPendingSearch();
        const seq = searchSeq.current;
        setIsSearching(true);
        searchTimer.current = setTimeout(async () => {
            searchTimer.current = null;
            if (!selectedUid) {
                finishSearch(seq);
                return;
            }
            try {
                await dispatch(
                    OrganizationsActions.getAvailableLocations(selectedUid, {
                        searchTerm: value.trim() || undefined,
                    }),
                );
            } finally {
                finishSearch(seq);
            }
        }, SEARCH_DEBOUNCE_MS);
    }

    // Only the search still owning the flag may release it: a later keystroke
    // (or closing the modal) has already bumped the sequence and owns it now.
    function finishSearch(seq: number) {
        if (searchSeq.current === seq) setIsSearching(false);
    }

    function handleLoadMore() {
        // Belt and braces: the button is disabled while a search is pending,
        // but a queued click must not slip a stale-term page request through.
        if (!selectedUid || isSearching) return;
        dispatch(
            OrganizationsActions.getAvailableLocations(selectedUid, {
                searchTerm: storedSearchTerm ?? undefined,
                offset: available.length,
            }),
        );
    }

    function handleAdd(locationUid: string) {
        if (!selectedUid) return;
        dispatch(
            OrganizationsActions.addLocationToOrganization(
                selectedUid,
                locationUid,
                storedSearchTerm ?? undefined,
            ),
        );
        closeAdd();
    }

    function handleAddAll() {
        if (!selectedUid) return;
        dispatch(
            OrganizationsActions.addAllLocationsToOrganization(
                selectedUid,
                storedSearchTerm ?? undefined,
            ),
        );
        closeAdd();
    }

    function handleRemove(locationUid: string) {
        if (!selectedUid) return;
        dispatch(
            OrganizationsActions.removeLocationFromOrganization(
                selectedUid,
                locationUid,
            ),
        );
    }

    const locations = detail?.locations ?? [];

    return (
        <div className="d-flex flex-column">
            <div className="d-flex justify-content-end mb-2">
                <CapsuleButton
                    testId={"organization-locations-add"}
                    text={t("mod_organizations_add_location")}
                    onClick={openAdd}
                />
            </div>

            {locations.length === 0 ? (
                <div className="text-center text-muted py-3">
                    {t("mod_organizations_no_locations")}
                </div>
            ) : (
                locations.map((loc) => (
                    <div
                        key={loc.uid}
                        className="
                            d-flex
                            align-items-center
                            justify-content-between
                            border
                            rounded
                            p-2
                            mb-2
                            bg-white
                        "
                    >
                        <div className="font-weight-bold">{loc.name}</div>
                        <CapsuleButton
                            testId={`organization-locations-remove-${loc.uid}`}
                            icon={Icons.trash}
                            style={"DANGER"}
                            size={"SMALL"}
                            classNameExtra="flex-shrink-0"
                            // Not gated on ownership: the backend accepts the
                            // owner OR the member who contributed the location
                            // (_require_owner_or_contributor), and the detail
                            // does not say who contributed which one, so the
                            // UI cannot tell them apart. Disabling it would
                            // take away the only way a member has to undo a
                            // link — the one-way door the backend just closed.
                            // A member who really cannot gets error 780, now
                            // mapped to a readable message.
                            tooltip={t("mod_organizations_remove_location")}
                            onClick={() => handleRemove(loc.uid)}
                        />
                    </div>
                ))
            )}

            <CustomModal
                show={isAddOpen}
                onHide={closeAdd}
                scrollable
                title={t("mod_organizations_add_location")}
                content={
                    <div
                        className="d-flex flex-column"
                        style={{ minWidth: "min(35rem, 80vw)" }}
                    >
                        <div className="text-muted small mb-2">
                            {t(
                                "mod_organizations_add_location_requires_premium",
                            )}
                        </div>
                        <CustomSearchBar
                            className="mb-2"
                            value={search}
                            placeholder={t("global_search_placeholder")}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                        {available.length === 0 ? (
                            <div className="text-muted text-center py-3">
                                {t("mod_organizations_no_available_locations")}
                            </div>
                        ) : (
                            available.map((loc) => (
                                <div
                                    key={loc.uid}
                                    onClick={() => handleAdd(loc.uid)}
                                    className="
                                        d-flex
                                        align-items-center
                                        justify-content-between
                                        border
                                        rounded
                                        p-2
                                        mb-2
                                        cp
                                        c-hover-240
                                    "
                                >
                                    <div className="font-weight-bold">
                                        {loc.name}
                                    </div>
                                </div>
                            ))
                        )}
                        {available.length < availableTotal && (
                            <div className="d-flex justify-content-center mt-1">
                                <CapsuleButton
                                    testId={"organization-locations-show-more"}
                                    text={t("global_show_more")}
                                    style={"INFO"}
                                    isEnabled={!isSearching}
                                    onClick={handleLoadMore}
                                />
                            </div>
                        )}
                    </div>
                }
                footer={
                    <div className="d-flex">
                        <CapsuleButton
                            text={t("global_cancel")}
                            style={"INFO"}
                            onClick={closeAdd}
                        />
                        {availableTotal > 0 && (
                            <>
                                <div className="ml-2" />
                                <CapsuleButton
                                    text={t(
                                        "mod_organizations_add_all_locations",
                                        { count: availableTotal },
                                    )}
                                    onClick={handleAddAll}
                                />
                            </>
                        )}
                    </div>
                }
            />
        </div>
    );
}
